import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getContact, createContact, updateContact, ContactCreate, ContactUpdate } from '../../api/data';

const ContactForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    unit: '',
    name_ar: '',
    name_en: '',
    phone: '',
    email: '',
    hours: '',
    location: ''
  });

  useEffect(() => {
    if (isEdit && id) {
      loadContact(parseInt(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadContact = async (contactId: number) => {
    try {
      const contact = await getContact(contactId);
      setFormData({
        unit: contact.unit,
        name_ar: contact.name_ar || '',
        name_en: contact.name_en || '',
        phone: contact.phone || '',
        email: contact.email || '',
        hours: contact.hours || '',
        location: contact.location || ''
      });
    } catch (error) {
      alert('Failed to load contact');
      navigate('/data/contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit && id) {
        const data: ContactUpdate = { ...formData };
        Object.keys(data).forEach(key => {
          if (data[key as keyof ContactUpdate] === '') {
            data[key as keyof ContactUpdate] = undefined;
          }
        });
        await updateContact(parseInt(id), data);
        alert('Contact updated successfully');
      } else {
        const data: ContactCreate = { ...formData };
        await createContact(data);
        alert('Contact created successfully');
      }
      
      navigate('/data/contacts');
    } catch (error: any) {
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
        <div className="card p-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-primary-500">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold text-primary-900 tracking-tight leading-tight">
          {isEdit ? 'Edit Contact' : 'Create Contact'}
        </h1>
        <Link to="/data/contacts" className="btn-ghost text-xs gap-1">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="card p-6 space-y-5">
        <div>
          <label className="label">Unit <span className="text-error">*</span></label>
          <input
            type="text"
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            required
            className="input-base"
            placeholder="Department or Unit Name"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Arabic Name</label>
            <input
              type="text"
              value={formData.name_ar}
              onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
              className="input-base"
              placeholder="الاسم بالعربية"
            />
          </div>
          <div>
            <label className="label">English Name</label>
            <input
              type="text"
              value={formData.name_en}
              onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
              className="input-base"
              placeholder="Name in English"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Phone</label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input-base"
              placeholder="+970-9-1234567"
            />
          </div>
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input-base"
              placeholder="contact@najah.edu"
            />
          </div>
        </div>

        <div>
          <label className="label">Office Hours</label>
          <textarea
            value={formData.hours}
            onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
            rows={3}
            className="input-base"
            placeholder="Sunday - Thursday: 8:00 AM - 4:00 PM"
          />
        </div>

        <div>
          <label className="label">Location</label>
          <textarea
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            rows={2}
            className="input-base"
            placeholder="Building name, floor, room number"
          />
        </div>

        <div className="flex gap-3 pt-3">
          <button type="submit" disabled={saving} className="btn-primary gap-1.5">
            {saving ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Saving...
              </>
            ) : (isEdit ? 'Update Contact' : 'Create Contact')}
          </button>
          <Link to="/data/contacts" className="btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
